import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";
import { BaseURI } from "../shared";

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const socket = useRef(null);
  const [connected, setConnected] = useState(false);
  const reconnectAttempts = useRef(0);
  const eventHandlers = useRef(new Map());

  const onStoreStatusUpdate = (callback) => {
    if (socket.current) {
      socket.current.on("store_status_update", callback);
      return () => {
        socket.current.off("store_status_update", callback);
      };
    }
  };
  useEffect(() => {
    console.log("🔌 Initializing Socket Connection...");

    socket.current = io(BaseURI, {
      transports: ["websocket"],
      reconnection: true,
      withCredentials: true,
    });

    const s = socket.current;

    s.on("connect", () => {
      console.log("✅ Socket connected:", s.id);
      setConnected(true);
      reconnectAttempts.current = 0;
    });

    s.on("disconnect", (reason) => {
      console.log("❌ Socket disconnected:", reason);
      setConnected(false);
    });

    s.on("reconnect", (attemptNumber) => {
      console.log("🔄 Socket reconnected after", attemptNumber, "attempts");
      setConnected(true);
      reconnectAttempts.current = 0;
    });

    s.on("reconnect_attempt", (attemptNumber) => {
      console.log("🔄 Attempting to reconnect...", attemptNumber);
      reconnectAttempts.current = attemptNumber;
    });

    s.on("reconnect_error", (error) => {
      console.error("❌ Reconnection error:", error);
    });

    s.on("reconnect_failed", () => {
      console.error("❌ Reconnection failed after max attempts");
    });

    s.on("connect_error", (error) => {
      console.error("❌ Connection error:", error);
    });

    s.on("ping", () => {
      console.log("🏓 Ping received");
    });

    s.on("pong", (latency) => {
      console.log("🏓 Pong received, latency:", latency, "ms");
    });

    return () => {
      console.log("🔌 Cleaning up socket connection...");
      s.removeAllListeners();
      s.disconnect();
    };
  }, []);
  useEffect(() => {
    if (!connected) return;

    const testConnection = () => {
      console.log("🔍 Testing socket connection...");
      socket.current?.emit("ping", Date.now());
    };

    const interval = setInterval(testConnection, 30000);
    return () => clearInterval(interval);
  }, [connected]);
  const joinRoom = (roomId) => {
    if (connected && socket.current) {
      console.log(`📦 Joining room: ${roomId}`);
      socket.current.emit("join_room_orders", roomId);
    }
  };
  const joinStoreRoom = (storeId) => {
    if (connected && socket.current) {
      console.log(`🏬 Joining store room: ${storeId}`);
      socket.current.emit("join", storeId);
    }
  };
  const addEventListener = (eventName, handler) => {
    if (socket.current) {
      if (eventHandlers.current.has(eventName)) {
        socket.current.off(eventName, eventHandlers.current.get(eventName));
      }

      socket.current.on(eventName, handler);
      eventHandlers.current.set(eventName, handler);

      console.log(`👂 Event listener added for: ${eventName}`);
    }
  };
  const removeEventListener = (eventName) => {
    if (socket.current && eventHandlers.current.has(eventName)) {
      socket.current.off(eventName, eventHandlers.current.get(eventName));
      eventHandlers.current.delete(eventName);
      console.log(`🔇 Event listener removed for: ${eventName}`);
    }
  };
  const emit = (eventName, data) => {
    if (socket.current && connected) {
      console.log(`📤 Emitting event: ${eventName}`, data);
      socket.current.emit(eventName, data);
    }
  };
  const [listeners] = useState(new Map());
  const addListener = (event, callback) => {
    if (!socket.current) return;
    if (listeners.has(event)) {
      socket.current.off(event, listeners.get(event));
    }
    socket.current.on(event, callback);
    listeners.set(event, callback);
    return () => {
      socket.current?.off(event, callback);
      listeners.delete(event);
    };
  };

  const value = {
    socket: socket.current,
    connected,
    reconnectAttempts: reconnectAttempts.current,
    joinRoom,
    joinStoreRoom,
    addEventListener,
    removeEventListener,
    emit,
    onStoreStatusUpdate,
    addListener,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
